// ─────────────────────────────────────────────
//  TV info-panel derivations — pure, Firestore-free, React-free shaping for the
//  TV-mode side panels (MD Call roster, CRNA roster by shift). Unit-testable.
//
//    • buildMdCallList   — today's AIC + supervising doctors, in call order,
//                          each with their phone (resolved from the staff pool).
//    • buildCrnaShiftGroups — today's CRNAs bucketed by leave time
//                          (3 PM / 5 PM / 7 PM / 9 PM …), names + time only.
// ─────────────────────────────────────────────
import { goHomeHourToClock, shiftCodeEndHour24, sortDoctorsByCallOrder } from "./schedule";
// ⚠️ IMPORTED, NOT COPIED. This file carried a hand-written twin of
// `isOnCallShift` "so the TV panel and the Daily roster agree" — two copies of
// one rule, which is the arrangement that guarantees they eventually will not.
// The sheet importer needed the same question answered in 2026-09-13 and that
// made three copies; there is now one.
import { isOnCallShift } from "./timeline";

// Resolve a CRNA's leave value (string shift code OR legacy numeric goHomeHour)
// to a 24h end hour — mirrors schedule.resolveEndHour24 using exported fns so
// every shift code ("3", "3L", "11a-7p", …) and legacy number maps correctly.
function endHour24(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const h = shiftCodeEndHour24(value);
    if (h != null) return h;
    return goHomeHourToClock(value).hour24; // numeric-as-string fallback
  }
  return goHomeHourToClock(value).hour24;
}
const pad2 = (n) => String(n).padStart(2, "0");

// Resolve a roster member's phone from the staff pool by id (or uid).
function phoneOf(staff, id) {
  const entry = (staff || []).find((s) => s && (s.id === id || s.uid === id));
  return (entry && entry.phone) || "";
}

// MD Call roster: the AIC + supervising doctors, ordered by call number (same
// as the daily roster). The AIC is merged with their supervisor entry so their
// real call number is kept (the AIC can also hold a call number, e.g. #14) and
// they are flagged isAic. A doctor with no call number sorts last. Each:
// { id, name, phone, callOrder, isAic }. Deduped by id.
export function buildMdCallList(roster, staff = []) {
  const aicId = roster?.aicId || null;
  const byId = new Map();
  const add = (m) => {
    if (!m || !m.id) return;
    const prev = byId.get(m.id) || {};
    byId.set(m.id, {
      id: m.id,
      name: m.name || prev.name || "",
      // A finite callOrder from any source wins; otherwise keep what we had.
      callOrder: Number.isFinite(m.callOrder) ? m.callOrder : (prev.callOrder ?? null),
      isAic: prev.isAic || m.id === aicId,
    });
  };
  if (aicId) add({ id: aicId, name: roster?.aicName });
  for (const s of roster?.supervisors || []) add(s);
  const list = Array.from(byId.values()).map((m) => ({ ...m, phone: phoneOf(staff, m.id) }));
  return sortDoctorsByCallOrder(list); // by call number; no-number entries last
}

// CRNA roster grouped by leave time. Standard buckets 15/17/19/21 → "3 PM" …
// "9 PM"; any other hour gets its own bucket labelled by its clock time; CRNAs
// with no resolvable leave time fall into an "Unscheduled" group last. Groups
// are returned ascending by hour (Unscheduled last). Each member: { id, name,
// timeLabel }.
const HOUR_LABEL = { 15: "3 PM", 17: "5 PM", 19: "7 PM", 21: "9 PM" };

// ── MD Call removal (TV X button) helpers ─────
// Resolve a sheet code ("DAZ") to its staff profile via shortCode.
export function resolveMdCallStaff(code, staff = []) {
  const c = String(code || "").toUpperCase();
  if (!c) return null;
  return (staff || []).find((s) => String(s?.shortCode || "").toUpperCase() === c) || null;
}

const stripHonorific = (n) => String(n || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();

// Does this room's supervising doctor sit in the pending-home list
// (roster.mdCallHome)? Matched by staff id (code → shortCode → staff doc) or,
// failing that, by honorific-insensitive name. Returns { code, name, staffId }
// or null — drives the room card's "Supervisor not available" message.
export function removedSupervisorForRoom(room, assignment, mdCallHome = [], staff = []) {
  const home = Array.isArray(mdCallHome) ? mdCallHome : [];
  if (!home.length) return null;
  const docId = room?.supervisingDoctorId || assignment?.attendingId || null;
  const docName = stripHonorific(room?.supervisingDoctorName || assignment?.attendingName);
  for (const e of home) {
    const st = resolveMdCallStaff(e.code, staff);
    if (docId && st && (st.id === docId || st.uid === docId)) {
      return { code: e.code, name: e.name || st.name || e.code, staffId: st.id };
    }
    const nm = stripHonorific(e.name || st?.name);
    if (nm && docName && nm === docName) {
      return { code: e.code, name: e.name || st?.name || e.code, staffId: st?.id || null };
    }
  }
  return null;
}

export function buildCrnaShiftGroups(roster, assignments = []) {
  // Resolve each CRNA's leave time the SAME way the board/Daily grid does so the
  // TV panel never disagrees with the room cards: the assignment row's shift code
  // (ahpShiftCode, keyed by the CRNA's id) wins, then the roster member's own
  // shiftCode, then a legacy goHomeHour. Single source = today's daily list.
  const shiftByAhp = new Map();
  for (const a of assignments || []) {
    if (a && a.ahpId && a.ahpShiftCode != null) shiftByAhp.set(a.ahpId, a.ahpShiftCode);
  }
  const buckets = new Map(); // key -> { hour, onCall, label, members }
  // ACTIVE roster only: anyone sent home (departed) leaves the TV CRNA panel;
  // confirm-home removes them from roster.crnas entirely. Cross-facility
  // arrivals live in roster.crnas like everyone else, so they show here.
  const departed = roster?.departed || {};
  for (const c of roster?.crnas || []) {
    if (!c || !c.id) continue;
    if (departed[c.id]) continue;
    const val = shiftByAhp.has(c.id)
      ? shiftByAhp.get(c.id)
      : (c.shiftCode != null ? c.shiftCode : c.goHomeHour);
    // On-call 24h shift gets its own group with the same "On call · 24 hrs"
    // label and "7a–7a" time the Daily roster view shows (Grace Elaro etc.),
    // instead of being mis-bucketed to 7 AM by the range's end hour.
    const onCall = isOnCallShift(val);
    const hour24 = onCall ? null : endHour24(val);
    const key = onCall ? "oncall" : (hour24 == null ? "none" : hour24);
    const label = onCall
      ? "On call · 24 hrs"
      : (hour24 == null ? "Unscheduled" : (HOUR_LABEL[hour24] || `${pad2(hour24)}:00`));
    if (!buckets.has(key)) {
      buckets.set(key, { hour: hour24, onCall, label, members: [] });
    }
    buckets.get(key).members.push({
      id: c.id,
      name: c.name || "",
      timeLabel: onCall ? "7a–7a" : (hour24 == null ? "" : label),
    });
  }
  const groups = Array.from(buckets.values());
  // Order: timed groups ascending, then On call · 24 hrs, then Unscheduled.
  const rank = (g) => (g.onCall ? 1 : g.hour == null ? 2 : 0);
  groups.sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 0) return a.hour - b.hour; // both timed
    return 0;
  });
  for (const g of groups) g.members.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return groups;
}
